import React, { useEffect, useState } from "react";

import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { Modal } from "../shared-components/Modal";
import InputField from "../shared-components/InputField";
import Toggle from "../shared-components/Toggle";
import {
  CompanyCreationSetting,
  CreateCompanyInput,
  SETTING_TYPES_ENUM,
  SettingType,
} from "../api/Companies";

import { useNavigate, useParams } from "react-router-dom";
import {
  getSettingTypeValueForCompanyCreationSetting,
  getSettingValue,
  useCreateCompany,
  useLoadSettingTypes,
  useGoToCompany,
} from "../api/CompaniesApi";
import MultiCompanyIcon from "../shared-components/icons/MultiCompanyIcon";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";

import SkeletonSpacer from "../shared-components/SkeletonSpacer";

const emptyState: CreateCompanyInput = {
  company: {
    name: "",
    notes: "",
    modified_by: HARDCODED_ACTOR_UUID,
    review: true,
    chat_support: true,
    instant_fit_subscriber: false,
    consent_forms: false,
    additional_web_search: false,
    web_content_translation: false,
    report_analytics_charts: true,
  },
  company_settings_creation: [],
};

type Props = {
  companyType: string;
  usePlus?: boolean;
};

export const AddCompanyButton = ({ companyType, usePlus }: Props) => {
  const { companyId: companyUUID } = useParams();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const { goToCompany, goToSubCompany } = useGoToCompany();

  const {
    isLoading: isLoadingCreate,
    mutate: create,
    error: errorCreate,
    data: createdCompany,
  } = useCreateCompany();

  const {
    isLoading: isLoadingSettingTypes,
    data: settingTypes,
    mutate: loadSettingTypes,
    error: errorLoadingSettingTypes,
  } = useLoadSettingTypes();

  const [error, setError] = useState<string>("");
  const [defaultCanCreateSubCompanies, setDefaultCanCreateSubCompany] =
    useState(getSettingTypeForSubCompany(settingTypes)?.default === "true");
  const [createCompanyState, setCreateCompanyState] =
    useState<CreateCompanyInput>(emptyState);

  useEffect(() => {
    loadSettingTypes();
  }, [loadSettingTypes]);

  function validateSetup(): boolean {
    return (
      !!createCompanyState?.company?.name &&
      createCompanyState?.company?.name?.trim() !== ""
    );
  }

  function close() {
    setError("");
    setShowModal(false);
  }

  function getSettingTypeForSubCompany(
    settings: SettingType[] | undefined
  ): SettingType | undefined {
    const settingCanCreateSubComp = settings?.find(
      (st) => st.name === SETTING_TYPES_ENUM.CAN_CREATE_COMPANY
    );
    return settingCanCreateSubComp;
  }

  useEffect(() => {
    if (!settingTypes) return;

    const settings: CompanyCreationSetting[] = settingTypes.map((st) => {
      const defaultValue = getSettingValue(
        st.name as SETTING_TYPES_ENUM,
        settingTypes,
        []
      );
      if (st.name === SETTING_TYPES_ENUM.CAN_CREATE_COMPANY) {
        setDefaultCanCreateSubCompany(defaultValue);
        return {
          setting_type_uuid: st.external_id,
          value: defaultValue ? "true" : "false",
        };
      }
      return { setting_type_uuid: st.external_id, value: st.default };
    });

    setCreateCompanyState({
      company: { ...emptyState.company },
      company_settings_creation: settings,
    });
  }, [settingTypes]);

  useEffect(() => {
    if (createdCompany) {
      close();
      if (companyType === "Company") {
        goToCompany(createdCompany.external_id, "checktypes");
      } else {
        goToSubCompany(
          companyUUID ?? "",
          createdCompany.external_id,
          "checktypes"
        );
      }
    }
  }, [
    createdCompany,
    companyType,
    navigate,
    companyUUID,
    defaultCanCreateSubCompanies,
    goToCompany,
    goToSubCompany,
  ]);

  useEffect(() => {
    if (errorCreate) {
      // @ts-ignore
      setError(errorCreate.message);
    }
  }, [errorCreate, setError, createdCompany]);

  function handleCreate() {
    if (validateSetup()) {
      setError("");
      const createObj = {
        company: {
          ...createCompanyState.company,
          name: createCompanyState?.company.name.trim() || "",
        },
        company_settings_creation:
          createCompanyState?.company_settings_creation || [],
      };

      if (companyUUID) {
        createObj.company.parent_company_uuid = companyUUID;
        create(createObj);
        return;
      }

      create(createObj);
    } else {
      setError("Ensure company name is filled");
    }
  }

  // TO DO: make the error disappear upon entering a valid name in the input field
  function handleNameChange(name: string) {
    if (!createCompanyState) return;
    const company = {
      ...createCompanyState.company,
      name,
    };

    setCreateCompanyState({
      ...createCompanyState,
      company,
    });
  }

  function handleCreateSubcompanies(canCreateSubCompanies: boolean) {
    if (!settingTypes) return;

    const settingCanCreateSubComp = getSettingTypeForSubCompany(settingTypes);
    const companyCreationSettings =
      createCompanyState?.company_settings_creation.map((ct) => {
        if (ct.setting_type_uuid === settingCanCreateSubComp?.external_id) {
          return {
            ...ct,
            value: getSettingValue(
              SETTING_TYPES_ENUM.CAN_CREATE_COMPANY,
              settingTypes,
              [
                {
                  setting_type_uuid: ct.setting_type_uuid,
                  value: canCreateSubCompanies,
                },
              ]
            ),
          };
        }
        return ct;
      }) ?? [];

    const tranformedSettings = getSettingTypeValueForCompanyCreationSetting(
      settingTypes,
      companyCreationSettings
    );

    setCreateCompanyState({
      ...createCompanyState,
      company_settings_creation: tranformedSettings,
    });
  }

  if (isLoadingSettingTypes) return <SkeletonSpacer />;

  return (
    <div>
      <Button
        text={`Add ${companyType}`}
        onClick={() => setShowModal(true)}
        type={usePlus ? ButtonTypes.add : ButtonTypes.default}
      />
      <Modal open={showModal} onClose={close} width={"w-full max-w-lg p-3"}>
        {/* Column Holder for icon and rest of content */}
        <div className="flex flex-col">
          <div className="flex flex-row space-x-3">
            {/* Icon Column */}
            <div>
              <MultiCompanyIcon />
            </div>
            {/* Rest of Content Column */}
            <div className="space-y-4">
              {/* Create Company Row */}
              <div>Create {companyType}</div>
              {/* Company Name and Input Field Row */}
              <div className="text-slate-600">
                {companyType} Name
                <InputField
                  name="Company"
                  placeholder="Type here..."
                  handleChange={(e) => handleNameChange(e.target.value)}
                ></InputField>
              </div>
              {/* Toggle Row for Add Company Modal */}
              {companyType === "Company" && (
                <div className="text-slate-600">
                  <Toggle
                    labelClassName="w-56 text-sm font-medium text-slate-600"
                    label="Can Create Sub-Companies"
                    onChange={handleCreateSubcompanies}
                    defaultValue={defaultCanCreateSubCompanies}
                  ></Toggle>
                </div>
              )}
              {/* Reminder and buttons Row */}
              <div>
                <div className="text-slate-500 text-sm">
                  Please make sure to assign at least one blueprint to your new
                  company in the next screen.
                </div>
                <div className="flex-col">
                  {/* Error message on top of buttons */}
                  <div className="flex justify-start gap-4 pt-6">
                    {errorLoadingSettingTypes && (
                      <ErrorMessageContainer message="Error loading setting types" />
                    )}
                    {""}
                  </div>
                  {error && <ErrorMessageContainer message={error} />}
                  {/* Buttons */}

                  {isLoadingCreate && <LoadingSpinner />}
                  {!isLoadingCreate && (
                    <div className="flex justify-end gap-4 pt-6">
                      <Button
                        text="Cancel"
                        colorType={ButtonColorTypes.cancel}
                        onClick={() => {
                          close();
                        }}
                      />
                      <Button
                        text={`Create ${companyType}`}
                        onClick={() => {
                          handleCreate();
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
